<!-- Define our page card. -->
<mat-card class="mat-page-card">

  <!-- Define the header of our page card. -->
  <mat-card-header>

    <!-- Give our page an icon. -->
    <mat-icon mat-card-avatar color="icon-orange">difference</mat-icon>

    <!-- Give our page a main title. -->
    <mat-card-title>
      PEO Client-Customer Mapping Import Wizard
    </mat-card-title>

    <!-- Give our page a very short description. -->
    <mat-card-subtitle>
      Use this wizard to step through importing PEO Client-Customer mappings from an uploaded CSV.
    </mat-card-subtitle>
  </mat-card-header>

  <!-- Define the content of our page card. -->
  <mat-card-content class="app-stepper-container">
    <mat-stepper [animationDuration]="'1000'" [linear]="true" #importWizardStepper>

      <!-- Upload and Consume CSV File -->
      <mat-step [editable]="false" [stepControl]="fileUploadFormGroup" label="Upload">
        <div class="app-stepper-description">
          <p>
            Use the form below to select a CSV file to upload and parse. Only CSV files are accepted at this time and
            must contain a column representing the <code>PeoId</code> from PrismHR, a column representing the
            <code>ClientId</code> from PrismHR and a column representing the <code>CustomerId</code> from within
            SyncStream. The CSV may have any number of columns and rows so long as there are values that match up with
            the three aforementioned columns.
          </p>
        </div>
        <div class="app-stepper-section">
          <form [formGroup]="fileUploadFormGroup" class="app-form">
            <fieldset>
              <legend>File Selection</legend>
              <input type="file" (change)="selectedCsvFile(csvFileInput)" accept="text/csv" hidden #csvFileInput>
              <mat-form-field class="app-form-field-whole">
                <input matInput placeholder="No CSV file selected." type="text" (click)="csvFileInput.click()"
                       [value]="csvFile?.name" readonly>
              </mat-form-field>
            </fieldset>
            <fieldset>
              <button mat-flat-button (click)="csvFileInput.click()" class="app-button-half"
                      matTooltip="Select a CSV file to upload and parse.">
                <mat-icon>upload_file</mat-icon>
                Select File
              </button>
              &nbsp;
              &nbsp;
              <button mat-flat-button (click)="uploadCsvFile(csvFileInput)" [disabled]="!csvFile || csvFileParsing"
                      class="app-button-half btn-light-blue"
                      matTooltip="Upload the selected CSV file and parse it.">
                <mat-icon>cloud_upload</mat-icon>
                Upload &amp; Parse File
              </button>
            </fieldset>
            <fieldset>
              <legend>Upload &amp; Parse Status</legend>
              <mat-progress-bar [mode]="csvFileParsing ? 'indeterminate' : 'determinate'"
                                [value]="!csvFileParsing ? csvFileUploadProgress : 0"
                                color="warn"></mat-progress-bar>
            </fieldset>
          </form>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-buttons">
          <button mat-button (click)="confirmStartOver()" color="icon-orange"
                  matTooltip="Discard all changes and start over.">
            <mat-icon>restart_alt</mat-icon>
            Start Over
          </button>
          &nbsp;
          &nbsp;
          <button mat-flat-button matStepperNext
                  [disabled]="!fileUploadFormGroup.valid || csvFileParsing || !csv.length || !csvHeaders.length"
                  color="primary" matTooltip="Continue to Column Selection.">Continue
          </button>
        </div>
      </mat-step>

      <!-- Select Columns for Mapping -->
      <mat-step [editable]="false" [stepControl]="columnSelectionFormGroup" label="Column Selection">
        <div class="app-stepper-description">
          <p>
            Use the form below to select which columns from the CSV match up with the <code>PeoId</code>
            and <code>ClientId</code> from PrismHR along with the <code>CustomerId</code> from SyncStream.
            The example data table shows what values are in which columns of the CSV, this is an inclusive list
            meaning all columns from the uploaded CSV are available for viewing. Once a column header is selected,
            it is no longer available for selection. If you need to reset your selections, please click the
            <code>Reset Column Selection</code> button below.
          </p>
        </div>
        <div class="app-stepper-section">
          <form [formGroup]="columnSelectionFormGroup" class="app-form">
            <fieldset>
              <legend>Column Mapping Selection</legend>
              <mat-form-field class="app-form-field-third" floatLabel="always">
                <mat-label>PrismHR PEO ID Column</mat-label>
                <mat-select (selectionChange)="circulateSelectedColumns()"
                            [formControl]="columnSelectionFormGroup.controls.peoIdColumn">
                  <mat-option value="-1">Select a Column</mat-option>
                  <mat-option *ngFor="let h of csvHeadersFilteredPeoId | keyvalue" [value]="h.key">{{ h.value }}
                  </mat-option>
                </mat-select>
                <mat-error
                  *ngIf="columnSelectionFormGroup.controls.peoIdColumn?.hasError('required') || columnSelectionFormGroup.controls.peoIdColumn?.hasError('min')">
                  This field is required, otherwise the CSV cannot be mapped.
                </mat-error>
                <mat-hint>Select the Column from the uploaded CSV that contains the PrismHR PEO ID.</mat-hint>
              </mat-form-field>
              <mat-form-field class="app-form-field-third" floatLabel="always">
                <mat-label>PrismHR Client ID Column</mat-label>
                <mat-select (selectionChange)="circulateSelectedColumns()"
                            [formControl]="columnSelectionFormGroup.controls.clientIdColumn">
                  <mat-option value="-1">Select a Column</mat-option>
                  <mat-option *ngFor="let h of csvHeadersFilteredClientId | keyvalue;" [value]="h.key">{{ h.value }}
                  </mat-option>
                </mat-select>
                <mat-error
                  *ngIf="columnSelectionFormGroup.controls.clientIdColumn?.hasError('required') || columnSelectionFormGroup.controls.clientIdColumn?.hasError('min')">
                  This field is required, otherwise the CSV cannot be mapped.
                </mat-error>
                <mat-hint>Select the Column from the uploaded CSV that contains the PrismHR Client ID.</mat-hint>
              </mat-form-field>
              <mat-form-field class="app-form-field-third" floatLabel="always">
                <mat-label>SyncStream Customer ID Column</mat-label>
                <mat-select (selectionChange)="circulateSelectedColumns()"
                            [formControl]="columnSelectionFormGroup.controls.customerIdColumn">
                  <mat-option value="-1">Select a Column</mat-option>
                  <mat-option *ngFor="let h of csvHeadersFilteredCustomerId | keyvalue" [value]="h.key">{{ h.value }}
                  </mat-option>
                </mat-select>
                <mat-error
                  *ngIf="columnSelectionFormGroup.controls.customerIdColumn?.hasError('required') || columnSelectionFormGroup.controls.customerIdColumn?.hasError('min')">
                  This field is required, otherwise the CSV cannot be mapped.
                </mat-error>
                <mat-hint>Select the Column from the uploaded CSV that contains the SyncStream Customer ID.</mat-hint>
              </mat-form-field>
            </fieldset>
            <fieldset class="app-stepper-table-container">
              <legend>Example Data</legend>
              <table mat-table [dataSource]="csv.slice(0, 3)" class="mat-elevation-z10">
                <ng-container *ngFor="let h of csvHeaders; let i = index" [matColumnDef]="h">
                  <th mat-header-cell *matHeaderCellDef>{{ h }}</th>
                  <td mat-cell *matCellDef="let d">{{ d[h] }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="csvHeaders"></tr>
                <tr mat-row *matRowDef="let row; columns: csvHeaders;"></tr>
              </table>
            </fieldset>
          </form>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-buttons">
          <button mat-button (click)="confirmStartOver()" color="icon-orange"
                  matTooltip="Discard all changes and start over.">
            <mat-icon>restart_alt</mat-icon>
            Start Over
          </button>
          &nbsp;
          &nbsp;
          <button mat-button (click)="resetColumnSelection()"
                  [disabled]="columnSelectionFormGroup.controls.clientIdColumn.value < 0 && columnSelectionFormGroup.controls.customerIdColumn.value < 0 && columnSelectionFormGroup.controls.peoIdColumn.value < 0"
                  color="icon-slate"
                  matTooltip="Reset the column selections.">
            <mat-icon>history</mat-icon>
            Reset Column Selection
          </button>
          &nbsp;
          &nbsp;
          <button mat-flat-button matStepperNext (click)="checkMissingPeo()"
                  [disabled]="!columnSelectionFormGroup.valid" color="primary" matTooltip="Continue to PEOs.">
            Continue
          </button>
        </div>
      </mat-step>

      <!-- PEO Validation -->
      <mat-step [editable]="false" [stepControl]="peoValidationFormGroup" label="PEOs">
        <div class="app-stepper-description">
          <p *ngIf="missingPeo">
            The uploaded CSV filed contained some PEOs that aren't present in the database. Use the form below to
            complete
            their required information. Once their required information is entered click <code>Save PEOs</code> to
            commit
            them to the database and continue to the next step of the import wizard.
          </p>
          <p *ngIf="!missingPeo">
            The uploaded CSV file did not contain any PEOs that aren't present in the database. Please click
            <code>Continue</code> to move on to the next step of the import wizard.
          </p>
        </div>
        <div class="app-stepper-section">
          <form [formGroup]="peoValidationFormGroup" class="app-form">
            <div formArrayName="items">
              <fieldset *ngFor="let p of peoValidationFormGroup.controls.items.controls; let i = index">
                <legend>Missing PEO ID: {{ p.controls.id.value }}</legend>
                <mat-form-field class="app-form-field-half" floatLabel="always">
                  <mat-label>PrismHR PEO ID</mat-label>
                  <input matInput [formControl]="p.controls.id" placeholder="PrismHR PEO ID" readonly>
                  <mat-icon matSuffix
                            *ngIf="!missingPeoToggles[p.controls.id.value].saving && !missingPeoToggles[p.controls.id.value].saved"
                            color="icon-slate" matTooltip="Unsaved">save_as
                  </mat-icon>
                  <mat-icon matSuffix *ngIf="missingPeoToggles[p.controls.id.value].saved" color="icon-green"
                            matTooltip="Saved">check
                  </mat-icon>
                  <mat-spinner matSuffix *ngIf="missingPeoToggles[p.controls.id.value].saving" color="warn"
                               diameter="20"
                               mode="indeterminate" matTooltip="Saving"></mat-spinner>
                  <mat-error *ngIf="p.controls.id?.hasError('required')">
                    A unique PrismHR identifier for the PEO is required.
                  </mat-error>
                  <mat-hint>
                    This should be exactly what appears in PrismHR. If an asterisk &lpar;<code>&ast;</code>&rpar;
                    is in the ID in PrismHR, it must be included here as well.
                  </mat-hint>
                </mat-form-field>
                <mat-form-field class="app-form-field-half" floatLabel="always">
                  <mat-label>PrismHR Peo Name</mat-label>
                  <input matInput [formControl]="p.controls.name" placeholder="PrismHR PEO Name">
                  <mat-icon matSuffix
                            *ngIf="!missingPeoToggles[p.controls.id.value].saving && !missingPeoToggles[p.controls.id.value].saved"
                            color="icon-slate" matTooltip="Unsaved">save_as
                  </mat-icon>
                  <mat-icon matSuffix *ngIf="missingPeoToggles[p.controls.id.value].saved" color="icon-green"
                            matTooltip="Saved">check
                  </mat-icon>
                  <mat-spinner matSuffix *ngIf="missingPeoToggles[p.controls.id.value].saving" color="warn"
                               diameter="20" mode="indeterminate" matTooltip="Saving"></mat-spinner>
                  <mat-error *ngIf="p.controls.name?.hasError('required')">
                    A name for the PEO is required.
                  </mat-error>
                </mat-form-field>
              </fieldset>
            </div>
          </form>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-buttons">
          <button mat-button (click)="confirmStartOver()" [disabled]="missingPeoSaving" color="icon-orange"
                  matTooltip="Discard all changes and start over.">
            <mat-icon>restart_alt</mat-icon>
            Start Over
          </button>
          &nbsp;
          &nbsp;
          <button mat-flat-button *ngIf="missingPeo" (click)="saveMissingPeo()"
                  [class]="peoValidationFormGroup.controls.items.valid ? 'btn-green' : ''"
                  [color]="!peoValidationFormGroup.controls.items.valid ? 'icon-green' : ''"
                  [disabled]="!peoValidationFormGroup.controls.items.valid || missingPeoSaving || missingPeoSaved"
                  matTooltip="Save the missing PEOs to the database.">
            <mat-icon>save</mat-icon>
            Save PEOs
          </button>
          &nbsp;
          &nbsp;
          <button mat-flat-button matStepperNext (click)="loadCustomerMap()"
                  [disabled]="!peoValidationFormGroup.controls.items.valid || (missingPeo && !missingPeoSaved)"
                  color="primary" matTooltip="Continue to Review Split Client-Customers.">
            Continue
          </button>
        </div>
      </mat-step>

      <!-- Split Customers -->
      <mat-step [editable]="false" label="Review Split Clients">
        <div class="app-stepper-description">
          <p>
            Please review the <code>Split Client-Customers</code> table below. These client-customers will
            <strong>not</strong> be mapped in the current iteration of the integration as we don't have a third
            point of identity &lbrack;uniqueness&rbrack; to match up with from PrismHR. These will be ignored,
            however you will be given a chance to download a CSV of them at the end of the import process.
          </p>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-section">
          <p *ngIf="!totalSplitClientCustomers && !loadingSplitClientCustomers && !loadingCustomerMap"
             class="app-stepper-description">
            There are no <code>Split Client-Customers</code> to review. Please click <code>Continue</code> to move on
            to the next step of the import wizard.
          </p>
          <div *ngIf="totalSplitClientCustomers || loadingSplitClientCustomers || loadingCustomerMap"
               class="app-stepper-table-container">
            <div class="table-controls">

              <!-- Table Filter -->
              <mat-form-field>
                <mat-label>Search the table below</mat-label>
                <input matInput [(ngModel)]="splitClientCustomersFilterInput"
                       (keyup)="filterSplitClientCustomersTable($event)" type="text">

                <button *ngIf="splitClientCustomersFilterInput" mat-icon-button matSuffix
                        matTooltip="Clear table filter."
                        aria-label="Clear the Split Client-Customers table filter."
                        (click)="clearFilterOnSplitClientCustomersTable()">
                  <mat-icon>close</mat-icon>
                </button>
              </mat-form-field>

              <!-- Paginator -->
              <mat-paginator [pageSizeOptions]="[10, 25, 50, 75, 100, 250, 500, 1000, 5000, 10000]"
                             aria-label="Select page size of the table."></mat-paginator>
            </div>

            <table mat-table [dataSource]="splitClientCustomers" class="mat-elevation-z10">
              <ng-container matColumnDef="peoId">
                <th mat-header-cell *matHeaderCellDef>PrismHR PEO ID</th>
                <td mat-cell *matCellDef="let c">{{ c.peoId }}</td>
              </ng-container>
              <ng-container matColumnDef="clientId">
                <th mat-header-cell *matHeaderCellDef>PrismHR Client ID</th>
                <td mat-cell *matCellDef="let c">{{ c.clientId }}</td>
              </ng-container>
              <ng-container matColumnDef="customerId">
                <th mat-header-cell *matHeaderCellDef>SyncStream Customer ID</th>
                <td mat-cell *matCellDef="let c">{{ c.customerId }}</td>
              </ng-container>
              <ng-container matColumnDef="customerName">
                <th mat-header-cell *matHeaderCellDef>SyncStream Customer</th>
                <td mat-cell *matCellDef="let c">{{ c.customerName || 'Unmapped' }}</td>
              </ng-container>
              <ng-container matColumnDef="customerBrandName">
                <th mat-header-cell *matHeaderCellDef>SyncStream Brand</th>
                <td mat-cell *matCellDef="let c">{{ c.customerBrandName || 'Unmapped' }}</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="reviewTableColumns; sticky: true"></tr>
              <tr mat-row *matRowDef="let row; columns: reviewTableColumns"></tr>

              <!-- Row shown when there is no matching data. -->
              <tr class="mat-row" *matNoDataRow>
                <td class="mat-cell" *ngIf="!!splitClientCustomersFilterInput && !loadingSplitClientCustomers && !loadingCustomerMap"
                    colspan="5"
                    style="text-align: center;">No PEO Client-Customer found matching
                  "{{ splitClientCustomersFilterInput }}".
                </td>
                <td class="mat-cell" *ngIf="loadingSplitClientCustomers || loadingCustomerMap" colspan="8">
                    <span style="display: flex; justify-content: center; align-items: center; width: 100%;">
                      <mat-spinner color="warn" diameter="20"></mat-spinner>
                      &nbsp;
                      Loading
                    </span>
                </td>
              </tr>
            </table>
          </div>
        </div>
        <div class="app-stepper-buttons">
          <button mat-button (click)="confirmStartOver()" [disabled]="loadingSplitClientCustomers || loadingCustomerMap"
                  color="icon-orange" matTooltip="Discard all changes and start over.">
            <mat-icon>restart_alt</mat-icon>
            Start Over
          </button>
          &nbsp;
          &nbsp;
          <button mat-flat-button (click)="acknowledgeSplitClientCustomers(importWizardStepper)"
                  [disabled]="loadingSplitClientCustomers || loadingCustomerMap" color="primary"
                  matTooltip="Continue to Review Previously Mapped Client-Customers.">
            Continue
          </button>
        </div>
      </mat-step>

      <!-- Previously Mapped Customers -->
      <mat-step [editable]="false" label="Review Previously Mapped Clients">
        <div class="app-stepper-description">
          <p>
            Please review the <code>Previously Mapped Client-Customers</code> table below. These client-customers
            were mapped previously, either during an import process or manually. They too will be ignored during this
            import process. If any of these mappings are incorrect, please correct them individually using the
            <a routerLink="/admin/peo" target="_blank">PEO interface</a> by selecting the <code>Clients</code> icon for
            the associated PEO and clicking the <code>Edit</code> icon for the client-customer in question. You will
            have the chance to download a CSV of these client-customers at the end of the import process.
          </p>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-section">
          <p *ngIf="!totalPreviouslyMappedClientCustomers && !loadingPreviouslyMappedClientCustomers && !loadingCustomerMap"
             class="app-stepper-description">
            There are no <code>Previously Mapped Client-Customers</code> to review. Please click <code>Continue</code>
            to move on to the next step of the import wizard.
          </p>
          <div *ngIf="totalPreviouslyMappedClientCustomers || loadingPreviouslyMappedClientCustomers || loadingCustomerMap"
               class="app-stepper-table-container">
            <div class="table-controls">

              <!-- Table Filter -->
              <mat-form-field>
                <mat-label>Search the table below</mat-label>
                <input matInput [(ngModel)]="previouslyMappedClientCustomersFilterInput"
                       (keyup)="filterPreviouslyMappedClientCustomersTable($event)" type="text">

                <button *ngIf="previouslyMappedClientCustomersFilterInput" mat-icon-button matSuffix
                        matTooltip="Clear table filter."
                        aria-label="Clear the Previously Mapped Client-Customers table filter."
                        (click)="clearFilterOnPreviouslyMappedClientCustomersTable()">
                  <mat-icon>close</mat-icon>
                </button>
              </mat-form-field>

              <!-- Paginator -->
              <mat-paginator [pageSizeOptions]="[10, 25, 50, 75, 100, 250, 500, 1000, 5000, 10000]"
                             aria-label="Select page size of the table."></mat-paginator>
            </div>

            <table mat-table [dataSource]="previouslyMappedClientCustomers" class="mat-elevation-z10">
              <ng-container matColumnDef="peoId">
                <th mat-header-cell *matHeaderCellDef>PrismHR PEO ID</th>
                <td mat-cell *matCellDef="let c">{{ c.peoId }}</td>
              </ng-container>
              <ng-container matColumnDef="clientId">
                <th mat-header-cell *matHeaderCellDef>PrismHR Client ID</th>
                <td mat-cell *matCellDef="let c">{{ c.clientId }}</td>
              </ng-container>
              <ng-container matColumnDef="customerId">
                <th mat-header-cell *matHeaderCellDef>SyncStream Customer ID</th>
                <td mat-cell *matCellDef="let c">{{ c.customerId }}</td>
              </ng-container>
              <ng-container matColumnDef="customerName">
                <th mat-header-cell *matHeaderCellDef>SyncStream Customer</th>
                <td mat-cell *matCellDef="let c">{{ c.customerName || 'Unmapped' }}</td>
              </ng-container>
              <ng-container matColumnDef="customerBrandName">
                <th mat-header-cell *matHeaderCellDef>SyncStream Brand</th>
                <td mat-cell *matCellDef="let c">{{ c.customerBrandName || 'Unmapped' }}</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="reviewTableColumns; sticky: true"></tr>
              <tr mat-row *matRowDef="let row; columns: reviewTableColumns"></tr>

              <!-- Row shown when there is no matching data. -->
              <tr class="mat-row" *matNoDataRow>
                <td class="mat-cell"
                    *ngIf="!!previouslyMappedClientCustomersFilterInput && !loadingPreviouslyMappedClientCustomers && !loadingCustomerMap"
                    colspan="5"
                    style="text-align: center;">No PEO Client-Customer found matching
                  "{{ previouslyMappedClientCustomersFilterInput }}".
                </td>
                <td class="mat-cell" *ngIf="loadingPreviouslyMappedClientCustomers || loadingCustomerMap" colspan="8">
                    <span style="display: flex; justify-content: center; align-items: center; width: 100%;">
                      <mat-spinner color="warn" diameter="20"></mat-spinner>
                      &nbsp;
                      Loading
                    </span>
                </td>
              </tr>
            </table>
          </div>
        </div>
        <div class="app-stepper-buttons">
          <button mat-button (click)="confirmStartOver()" [disabled]="loadingPreviouslyMappedClientCustomers || loadingCustomerMap"
                  color="icon-orange" matTooltip="Discard all changes and start over.">
            <mat-icon>restart_alt</mat-icon>
            Start Over
          </button>
          &nbsp;
          &nbsp;
          <button mat-flat-button matStepperNext [disabled]="loadingPreviouslyMappedClientCustomers || loadingCustomerMap"
                  color="primary" matTooltip="Continue to Review New Client-Customers.">
            Continue
          </button>
        </div>
      </mat-step>

      <!-- New Customers -->
      <mat-step [editable]="false" label="Review New Clients">
        <div class="app-stepper-description">
          <p>
            Please review the <code>New Client-Customers</code> table below. These clients will be imported into the
            database on the next step of this import process with the option to reconfigure the new client-customer
            print settings to the PrismHR 1095 Viewer. You will also have the option of downloading a CSV of these
            client-customers &lbrack;after saving them&rbrack; on the next step of this import process.
          </p>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-section">
          <p *ngIf="!totalNewClientCustomers && !loadingNewClientCustomers && !loadingCustomerMap"
             class="app-stepper-description">
            There are no <code>New Client-Customers</code> to review. We're going to need to start over with a CSV that
            contains new mappings in order to proceed. Please click <code>Start Over</code> to start this process over.
          </p>
          <div *ngIf="totalNewClientCustomers || loadingNewClientCustomers || loadingCustomerMap"
               class="app-stepper-table-container">
            <div class="table-controls">

              <!-- Table Filter -->
              <mat-form-field>
                <mat-label>Search the table below</mat-label>
                <input matInput [(ngModel)]="newClientCustomersFilterInput"
                       (keyup)="filterNewClientCustomersTable($event)" type="text">

                <button *ngIf="newClientCustomersFilterInput" mat-icon-button matSuffix
                        matTooltip="Clear table filter."
                        aria-label="Clear the New Client-Customers table filter."
                        (click)="clearFilterOnNewClientCustomersTable()">
                  <mat-icon>close</mat-icon>
                </button>
              </mat-form-field>

              <!-- Paginator -->
              <mat-paginator [pageSizeOptions]="[10, 25, 50, 75, 100, 250, 500, 1000, 5000, 10000]"
                             aria-label="Select page size of the table."></mat-paginator>
            </div>

            <table mat-table [dataSource]="newClientCustomers" class="mat-elevation-z10">
              <ng-container matColumnDef="_exclude">
                <th mat-header-cell *matHeaderCellDef>Exclude</th>
                <td mat-cell *matCellDef="let c">
                  <mat-slide-toggle
                    [(ngModel)]="clientCustomerToggles[getClientCustomerToggleKey(c)].exclude"
                    [checked]="clientCustomerToggles[getClientCustomerToggleKey(c)].exclude"
                    matTooltip="Exclude this client from the import.">
                  </mat-slide-toggle>
                </td>
              </ng-container>
              <ng-container matColumnDef="peoId">
                <th mat-header-cell *matHeaderCellDef>PrismHR PEO ID</th>
                <td mat-cell *matCellDef="let c">{{ c.peoId }}</td>
              </ng-container>
              <ng-container matColumnDef="clientId">
                <th mat-header-cell *matHeaderCellDef>PrismHR Client ID</th>
                <td mat-cell *matCellDef="let c">{{ c.clientId }}</td>
              </ng-container>
              <ng-container matColumnDef="customerId">
                <th mat-header-cell *matHeaderCellDef>SyncStream Customer ID</th>
                <td mat-cell *matCellDef="let c">{{ c.customerId }}</td>
              </ng-container>
              <ng-container matColumnDef="customerName">
                <th mat-header-cell *matHeaderCellDef>SyncStream Customer</th>
                <td mat-cell *matCellDef="let c">{{ c.customerName || 'Unmapped' }}</td>
              </ng-container>
              <ng-container matColumnDef="customerBrandName">
                <th mat-header-cell *matHeaderCellDef>SyncStream Brand</th>
                <td mat-cell *matCellDef="let c">{{ c.customerBrandName || 'Unmapped' }}</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="reviewTableColumnsForNewClientCustomers; sticky: true"></tr>
              <tr mat-row *matRowDef="let row; columns: reviewTableColumnsForNewClientCustomers"></tr>

              <!-- Row shown when there is no matching data. -->
              <tr class="mat-row" *matNoDataRow>
                <td class="mat-cell" *ngIf="!!newClientCustomersFilterInput && !loadingNewClientCustomers && !loadingCustomerMap"
                    colspan="5" style="text-align: center;">No PEO Client-Customer found matching
                  "{{ newClientCustomersFilterInput }}".
                </td>
                <td class="mat-cell" *ngIf="loadingNewClientCustomers || loadingCustomerMap" colspan="8">
                    <span style="display: flex; justify-content: center; align-items: center; width: 100%;">
                      <mat-spinner color="warn" diameter="20"></mat-spinner>
                      &nbsp;
                      Loading
                    </span>
                </td>
              </tr>
            </table>
          </div>
        </div>
        <div class="app-stepper-buttons">
          <span *ngIf="!totalNewClientCustomers">
            <button *ngIf="totalSplitClientCustomers" mat-button (click)="downloadSplitClientCustomersCsv()"
                    [disabled]="savingNewClientCustomers || loadingNewClientCustomers || loadingCustomerMap || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                    class="mat-icon-lavender" matTooltip="Download the CSV of Split Client-Customers.">
              <mat-icon>download</mat-icon>
              Split Client CSV
            </button>
            &nbsp;
            &nbsp;
            <button *ngIf="totalPreviouslyMappedClientCustomers" mat-button
                    (click)="downloadPreviouslyMappedClientCustomersCsv()"
                    [disabled]="savingNewClientCustomers || loadingNewClientCustomers || loadingCustomerMap || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                    class="mat-icon-lavender" matTooltip="Download the CSV of Previously Mapped Client-Customers.">
              <mat-icon>download</mat-icon>
              Previously Mapped Clients CSV
            </button>
            &nbsp;
            &nbsp;
          </span>
          <button mat-button (click)="confirmStartOver(!totalNewClientCustomers)"
                  [color]="totalNewClientCustomers ? 'icon-orange' : ''"
                  [class]="!totalNewClientCustomers ? 'btn-orange' : ''"
                  [disabled]="loadingNewClientCustomers || loadingCustomerMap"
                  [matTooltip]="totalNewClientCustomers ? 'Discard all changes and start over.' : 'Start Over with a new CSV.'">
            <mat-icon>restart_alt</mat-icon>
            Start Over
          </button>
          &nbsp;
          &nbsp;
          <button *ngIf="totalNewClientCustomers" mat-flat-button matStepperNext
                  [disabled]="loadingNewClientCustomers || loadingCustomerMap" color="primary" matTooltip="Continue to Save.">
            Continue
          </button>
        </div>
      </mat-step>

      <!-- Import New Customers -->
      <mat-step [editable]="false" label="Import">
        <div class="app-stepper-description">
          <p>
            This is the final step of the wizard. To commit the new client-customer mappings simply click the
            <code>Import New Mappings</code> button below. The option to reconfigure the new client-customer
            print settings to the PrismHR 1095 Viewer is also available and enabled by default. This option
            will reset the customer's print settings in SyncStream so please ensure the customer needs this.
            The options for downloading the CSV data sets from this import are also available below.
            <code>Split Clients</code> are the critical mappings that are not being imported.
            <code>Previously Mapped Clients</code> are mappings that have already been imported.
            <code>New Client Mappings</code> are the mappings that have been saved during this import process.
          </p>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-section">
          <div class="app-stepper-description">
            <mat-slide-toggle
              [(ngModel)]="savingNewClientCustomersIncludePrintSettings"
              [checked]="savingNewClientCustomersIncludePrintSettings"
              [disabled]="savedNewClientCustomers || savingNewClientCustomers || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers">
              Reconfigure Print Settings for 1095 Viewer
            </mat-slide-toggle>
            &nbsp;
            &nbsp;
            <button mat-button (click)="saveNewClientCustomers()"
                    [class]="!savingNewClientCustomers && !savedNewClientCustomers ? 'btn-green' : ''"
                    [color]="savedNewClientCustomers && !savingNewClientCustomers ? 'icon-green' : savingNewClientCustomers ? 'icon-slate' : ''"
                    [disabled]="savingNewClientCustomers || savedNewClientCustomers || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                    matTooltip="Import the new client customers into the database.">
              <mat-icon *ngIf="!savingNewClientCustomers && !savedNewClientCustomers">save</mat-icon>
              <mat-icon *ngIf="savedNewClientCustomers">check</mat-icon>
              <mat-spinner *ngIf="savingNewClientCustomers" color="warn" diameter="18"
                           mode="indeterminate"></mat-spinner>
              <span *ngIf="!savingNewClientCustomers && !savedNewClientCustomers">Import New Mappings</span>
              <span *ngIf="savedNewClientCustomers">New Mappings Imported</span>
            </button>
          </div>
        </div>
        <div class="app-stepper-section">&nbsp;</div>
        <div class="app-stepper-buttons">
          <span style="justify-content: flex-start;">
            <button *ngIf="totalSplitClientCustomers" mat-button (click)="downloadSplitClientCustomersCsv()"
                    [disabled]="savingNewClientCustomers || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                    class="mat-icon-lavender" matTooltip="Download the CSV of Split Client-Customers.">
              <mat-icon>download</mat-icon>
              Split Client CSV
            </button>
            &nbsp;
            &nbsp;
            <button *ngIf="totalPreviouslyMappedClientCustomers" mat-button
                    (click)="downloadPreviouslyMappedClientCustomersCsv()"
                    [disabled]="savingNewClientCustomers || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                    class="mat-icon-lavender" matTooltip="Download the CSV of Previously Mapped Client-Customers.">
              <mat-icon>download</mat-icon>
              Previously Mapped Clients CSV
            </button>
            &nbsp;
            &nbsp;
            <button mat-flat-button (click)="downloadNewClientCustomersCsv()"
                    [disabled]="!savedNewClientCustomers || savingNewClientCustomers || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                    class="mat-icon-lavender" matTooltip="Download the CSV of New Client-Customer Mappings.">
              <mat-icon>download</mat-icon>
              New Client Mappings CSV
            </button>
          </span>
          <button mat-button (click)="confirmStartOver()"
                  [disabled]="savingNewClientCustomers || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                  color="icon-orange"
                  matTooltip="Discard all changes and start over.">
            <mat-icon>restart_alt</mat-icon>
            Start Over
          </button>
          &nbsp;
          &nbsp;
          <button mat-flat-button (click)="confirmStartOver(savedNewClientCustomers)"
                  [disabled]="!savedNewClientCustomers || savingNewClientCustomers || downloadingCsvNewClientCustomers || downloadingCsvPreviouslyMappedClientCustomers || downloadingCsvSplitClientCustomers"
                  color="primary" matTooltip="Complete the Import Wizard.">
            Finish
          </button>
        </div>
      </mat-step>
    </mat-stepper>
  </mat-card-content>
</mat-card>
